import React, {useCallback, useState, useMemo, useEffect} from "react";
import {Form, DatePicker, Button, Collapse, Row, Col, Typography, Input, Switch, Tooltip, Spin} from "antd";
import {ProviderBidItems} from "./ProviderBidItems";
import {FormInputV2} from "../../components/form/index.jsx";
import {CustomTimePicker} from "../../components/CustomTimePicker.jsx";
import moment from "moment";
import {SimpleUploader} from "../../components/index.jsx";
import {openWindow} from "../../http/WindowOpener.jsx";
import {createUrlWithoutTransformations} from "../../utils/cloudinaryUtils.jsx";
import {TrashIcon} from "../../icons";
import {StyledButton} from "../../components";
import {ProviderPortfolioUploadModal} from "../../provider/ProviderPortfolioUploadModal";
import {
    UploadOutlined,
    QuestionCircleOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    LoadingOutlined
} from "@ant-design/icons";
import {MarketplaceImagesCarousel} from "../../marketplace/OrderImagesCarousel/MarketplaceImagesCarousel.jsx";
import {NOOP} from "../../utils/NOOP.jsx";
import {initialItem} from "./utils.jsx";
import {isProductHasShippingProvider} from "../../admin/upload/utils.js";
import {getLogger} from "../../Logger.jsx";
import classnames from "classnames";
import {StringBuilder} from "../../AppUtils.js";
import {themeNamesToText} from "../../consts.js";
import isMobileDevice from "is-mobile";

const {Panel} = Collapse;
const {TextArea} = Input;
const {Title} = Typography;

export const ProviderBidForm = ({
    formValues,
    setFormValues,
    brief,
    onSubmit = NOOP,
    bidHaveFiles = false,
    bidHaveOptions = false,
    bidProduct,
    loadingDeliveryPrice = false
}) => {
    const [activeKey, setActiveKey] = useState([]);
    const [portfolioVisibility, setPortfolioVisibility] = useState(false);
    const [isBidByFile, setIsBidByFile] = useState(bidHaveFiles ? true : bidHaveOptions ? false : true);
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [loading, setLoading] = useState(false);

    const [form] = Form.useForm();

    const log = useMemo(() => getLogger({}, "ProviderBidForm"), []);

    const isUploading = isUploadingFile || isUploadingPhoto || loading;

    const shouldHideDeliveryPrice = useMemo(() => isProductHasShippingProvider(bidProduct), [bidProduct]);

    const handleFieldChange = (field, value, isNumber = false) => {
        log("handleFieldChange", {field, value, isNumber});
        let newValue = value;
        if (isNumber) {
            if (value === "" || value === null || value === undefined || isNaN(value)) {
                newValue = undefined;
            } else {
                newValue = Number(value);
            }
        }

        setFormValues(prev => ({
            ...prev,
            [field]: newValue
        }));
    };

    const handleCollapseChange = keys => {
        setActiveKey(keys);
    };

    const isValidFileObject = file => {
        return (
            typeof file !== "string" &&
            typeof file === "object" &&
            typeof file.fileId === "string" &&
            typeof file.fileName === "string" &&
            typeof file.fileUrl === "string"
        );
    };

    const addUpgradeOptionsFile = useCallback(
        file => {
            log("addUpgradeOptionsFile", {file});
            const fileObject = {
                fileId: file.fileId || file.id || Date.now().toString(),
                fileName: file.fileName || file.name || file.originalName,
                fileUrl: file.fileUrl || file.url
            };

            setFormValues(prev => {
                const existingFiles = Array.isArray(prev.package?.files)
                    ? prev.package.files.filter(isValidFileObject)
                    : [];

                return {
                    ...prev,
                    package: {
                        ...prev.package,
                        files: [...existingFiles, fileObject]
                    }
                };
            });
            setIsUploadingFile(false);
        },
        [setFormValues]
    );

    const onRemoveFile = useCallback(
        fileId => {
            log("onRemoveFile", {fileId});
            setFormValues(prev => {
                const updatedFiles = Array.isArray(prev.package?.files)
                    ? prev.package.files.filter(f => f.fileId !== fileId)
                    : [];

                return {
                    ...prev,
                    package: {
                        ...prev.package,
                        files: updatedFiles
                    }
                };
            });
        },
        [log, setFormValues]
    );

    const handleSubmit = async values => {
        log("handleSubmit", {values});
        if (loading) return;
        setLoading(true);
        const submissionData = {
            ...values,
            package: {
                ...values.package,
                options: isBidByFile ? [] : values.package?.options || []
            }
        };
        await onSubmit(submissionData, isBidByFile);
        setLoading(false);
    };

    const handleBidModeToggle = useCallback(() => {
        log("handleBidModeToggle", {isBidByFile});
        const newIsBidByFile = !isBidByFile;
        setIsBidByFile(newIsBidByFile);

        if (!newIsBidByFile) {
            setFormValues(prev => ({
                ...prev,
                package: {
                    ...prev.package,
                    options: [initialItem],
                    files: []
                },
                price: 0
            }));
        } else {
            setFormValues(prev => ({
                ...prev,
                package: {
                    ...prev.package,
                    files: [],
                    options: []
                },
                price: undefined
            }));
        }
    }, [isBidByFile, log, setFormValues]);

    return (
        <div className="provider-bid-form-container">
            <ProviderPortfolioUploadModal
                onClose={() => {
                    setPortfolioVisibility(false);
                    setIsUploadingPhoto(false);
                }}
                onSave={portfolioImages => {
                    setFormValues(prev => ({
                        ...prev,
                        package: {
                            ...prev.package,
                            photos: [...(prev.package?.photos || []), ...portfolioImages]
                        }
                    }));
                    setPortfolioVisibility(false);
                    setIsUploadingPhoto(false);
                }}
                providerDetails={{...formValues, portfolioImages: formValues?.package?.photos}}
                providerNonExist={true}
                visible={portfolioVisibility}
            />
            <Form
                form={form}
                onFinish={handleSubmit}
                layout="vertical"
                fields={Object.keys(formValues).map(key => {
                    return {
                        name: [key],
                        value: formValues[key]
                    };
                })}
                className="form-container"
                initialValues={formValues}>
                <Title className="bid-form-titles">בניית הצעת המחיר ללקוח</Title>
                <Title className="bid-form-subtitles">
                    כאן תוכל לבנות את הצעת המחיר שתישלח ללקוח לאישור, יש להתייחס לבריף שציין
                </Title>

                <Row gutter={24}>
                    <Col span={8} xs={24} sm={8}>
                        <Form.Item
                            label="איפה"
                            className="label-text"
                            rules={[{required: true, message: "יש להזין כתובת"}]}>
                            <FormInputV2
                                value={brief?.address}
                                name={"address"}
                                disabled={true}
                                onChange={e => handleFieldChange("address", e.target.value)}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={8} xs={24} sm={8}>
                        <Form.Item
                            label="עלות משלוח"
                            className="label-text"
                            name="deliveryPrice"
                            validateStatus={formValues?.errors?.deliveryPrice ? "error" : ""}
                            help={formValues?.errors?.deliveryPrice}
                            rules={[{required: true, message: "יש להזין עלות משלוח"}]}>
                            <FormInputV2
                                className={classnames("provider-bid-input", {disabled: shouldHideDeliveryPrice})}
                                type="number"
                                disabled={shouldHideDeliveryPrice}
                                name={"deliveryPrice"}
                                value={formValues?.deliveryPrice}
                                onChange={e => handleFieldChange("deliveryPrice", e.target.valueAsNumber, true)}
                                suffix={
                                    loadingDeliveryPrice ? (
                                        <Spin indicator={<LoadingOutlined spin />} size="small" />
                                    ) : undefined
                                }
                                placeholder="הזן סכום כולל למשלוח"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={8} xs={24} sm={8}>
                        <Form.Item
                            label="חלון שילוח ההזמנה"
                            className="label-text"
                            name="dtstart"
                            validateStatus={formValues?.errors?.dtstart ? "error" : ""}
                            help={formValues?.errors?.dtstart}
                            style={{gap: 10, flexDirection: "column"}}
                            rules={[{required: true, message: "יש להזין מועד שילוח הזמנה"}]}>
                            <>
                                <Row style={{gap: 10}}>
                                    <Col xs={12} sm={12}>
                                        <DatePicker
                                            defaultValue={moment(formValues?.dtstart) ?? ""}
                                            format="DD.MM.YYYY"
                                            className="wb-brief-date-picker"
                                            style={{width: "100%", marginLeft: 10}}
                                            onChange={date =>
                                                handleFieldChange("dtstart", date ? moment(date).valueOf() : null)
                                            }
                                        />
                                    </Col>
                                    <Col xs={10} sm={10}>
                                        <CustomTimePicker
                                            style={{
                                                // width: "30%",
                                                height: "40px",
                                                fontSize: "14px",
                                                borderRadius: "5px"
                                            }}
                                            value={moment(formValues?.dtstart) ?? ""}
                                            format="HH:mm"
                                            suffixIcon={null}
                                            minuteStep={15}
                                            placeholder={"משעה"}
                                            showNow={true}
                                            onChange={time =>
                                                handleFieldChange("dtstart", time ? moment(time).valueOf() : null)
                                            }
                                        />
                                    </Col>
                                </Row>
                            </>
                        </Form.Item>
                    </Col>
                    <Col span={8} xs={24} sm={8}>
                        <Form.Item
                            label="מתאים למספר משתתפים"
                            className="label-text"
                            name="participantsAmount"
                            validateStatus={formValues?.errors?.participantsAmount ? "error" : ""}
                            help={formValues?.errors?.participantsAmount}
                            rules={[{required: true, message: "יש להזין כמות משתתפים"}]}>
                            <FormInputV2
                                type="number"
                                name={"participantsAmount"}
                                value={formValues?.participantsAmount}
                                onChange={e => handleFieldChange("participantsAmount", e.target.valueAsNumber, true)}
                                placeholder="הזן כמות"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Title className="bid-form-titles">תוכן הצעת המחיר</Title>
                <Title className="bid-form-subtitles">
                    בחרו בטוגל למטה כדי להחליט אם להעלות קובץ עם ההצעה או להזין את הפריטים באופן ידני.
                    <br />
                    שימו לב – יש להזין את המחירים כולל מע"מ.
                </Title>
                <div style={{display: "flex", alignItems: "center"}}>
                    <Switch
                        onChange={handleBidModeToggle}
                        checked={isBidByFile}
                        title={isBidByFile ? "הצעת מחיר לפי העלאת קובץ" : "הצעת מחיר לפי פריטים"}
                    />
                    <span
                        style={{
                            padding: 5,
                            fontSize: 20
                        }}>
                        {isBidByFile ? "הצעת מחיר לפי העלאת קובץ" : "הצעת מחיר לפי פריטים"}
                    </span>
                </div>

                {!isBidByFile && (
                    <Col span={24} xs={24}>
                        <ProviderBidItems
                            form={form}
                            formItems={formValues?.package?.options}
                            setFormValues={setFormValues}
                        />
                    </Col>
                )}

                {isBidByFile && (
                    <Row gutter={24} style={{marginTop: 20, alignItems: "flex-end"}}>
                        <Col span={12} xs={24} sm={12}>
                            <Form.Item
                                label="העלאת קובץ הצעת מחיר"
                                className="label-text"
                                name={["package", "files"]}
                                rules={[{required: true, message: "יש להעלות קובץ"}]}>
                                <div
                                    className="order-form-upgrade-options-file-line"
                                    style={{
                                        margin: 0,
                                        ...(isMobileDevice() && {fontSize: "12px", padding: "15px 5px"})
                                    }}>
                                    <SimpleUploader
                                        accept="application/pdf,image/*"
                                        onClose={() => {
                                            setIsUploadingFile(false);
                                        }}
                                        onFinish={file => {
                                            setIsUploadingFile(true);
                                            addUpgradeOptionsFile(file);
                                        }}
                                        multiple={false}
                                        disabled={
                                            Array.isArray(formValues?.package?.files) &&
                                            formValues?.package?.files.length > 0
                                        }>
                                        העלאת קובץ (PDF / תמונה)
                                    </SimpleUploader>
                                    <div className="order-form-upgrade-options-files-container">
                                        {Array.isArray(formValues?.package?.files) &&
                                            !!formValues?.package?.files.length &&
                                            formValues?.package?.files.map(customFile => (
                                                <span
                                                    key={customFile.fileId}
                                                    className="order-form-upgrade-options-file">
                                                    <span
                                                        onClick={() =>
                                                            openWindow(
                                                                createUrlWithoutTransformations(
                                                                    customFile.fileUrl,
                                                                    customFile.fileName
                                                                )
                                                            )
                                                        }>
                                                        {customFile.fileName}
                                                    </span>
                                                    <TrashIcon
                                                        style={{fill: "red"}}
                                                        onClick={() => onRemoveFile(customFile.fileId)}
                                                    />
                                                </span>
                                            ))}
                                    </div>
                                </div>
                            </Form.Item>
                        </Col>
                        <Col span={8} xs={24} sm={8}>
                            <Form.Item
                                validateStatus={formValues?.errors?.price ? "error" : ""}
                                help={formValues?.errors?.price}
                                name="price"
                                className="label-text"
                                label={"מחיר ההצעה הסופי כולל עמלה ומעמ"}
                                rules={[{required: true, message: "יש להזין סכום הצעת מחיר"}]}>
                                <FormInputV2
                                    name={"price"}
                                    type="number"
                                    value={formValues?.price}
                                    placeholder="הזן סכום הצעת מחיר"
                                    onChange={e => handleFieldChange("price", e.target.valueAsNumber, true)}
                                    style={{width: 200}}
                                />
                                <Tooltip title={"יש להזין את הסכום שמופיע בהצעת המחיר, זהו הסכום שהלקוח יראה ויאשר"}>
                                    <QuestionCircleOutlined className="form-modal-input-info" />
                                </Tooltip>
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <div className="system-notice">
                                <div>✅ כמה נקודות לתשומת ליבכם </div>
                                <ul>
                                    <li>
                                        זוהי הצעת מחיר ללקוח של <strong> {themeNamesToText[brief?.systemId]} </strong> -
                                        חשוב לוודא ששם הנמען מצוין בהתאם.
                                    </li>
                                    <li>
                                        יש שדה ייעודי למחיר עלות המשלוח במערכת — אין לצרף את עלות המשלוח בתוך הקובץ.
                                    </li>
                                    <li>המחירים שציינת בהצעה לוקחים בחשבון את העמלה שהוסכמה מול וולביז מע״מ.</li>
                                    <li>המחיר הסופי ללקוח צריך להיות כוללים מע״מ וזהים למחיר השוק.</li>
                                </ul>
                            </div>
                        </Col>
                    </Row>
                )}
                <div className="bid-price-notice">
                    יש לשים לב שהסכום הכולל של ההצעה לא יעלה על תקציב ההזמנה בניכוי עלות המשלוח.
                </div>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            label="דגשים נוספים"
                            className="label-text"
                            name="providerNote"
                            validateStatus={formValues?.errors?.providerNote ? "error" : ""}
                            help={formValues?.errors?.providerNote}>
                            <TextArea
                                value={formValues?.providerNote}
                                onChange={e => handleFieldChange("providerNote", e.target.value)}
                                rows={4}
                                className="label-text-area"
                                placeholder="כאן אפשר להקליד את ההצעה שלך או לתאר בקצרה את ההצעה "
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Title className="bid-form-titles">העלאת תמונות להמחשה</Title>
                <Form.Item className="label-text">
                    <div className="order-form-upgrade-options-file-line" style={{margin: 0}}>
                        <span
                            className="simple-uploader"
                            onClick={() => {
                                setIsUploadingPhoto(true);
                                setPortfolioVisibility(true);
                            }}>
                            <UploadOutlined style={{marginLeft: 5}} />
                            העלאות קבצי PNG/ JEPG
                        </span>
                    </div>
                </Form.Item>
                {!!formValues?.package?.photos?.length && (
                    <>
                        <Title className="bid-form-titles">גלריית תמונות</Title>
                        <MarketplaceImagesCarousel
                            log={NOOP}
                            images={formValues?.package?.photos.map(p => ({
                                id: p.imageId,
                                url: p.imageUrl,
                                key: p.imageUrl
                            }))}
                            showCarouselArrows={formValues?.package?.photos?.length > 1}
                            carouselProps={{infinite: true}}
                        />
                    </>
                )}
                <Collapse
                    bordered={false}
                    activeKey={activeKey}
                    onChange={handleCollapseChange}
                    expandIconPosition="end"
                    expandIcon={({isActive}) => (isActive ? <ArrowDownOutlined /> : <ArrowUpOutlined />)}
                    className="provider-brief-collapse">
                    <Panel header="מדיניות עריכות וביטולים של הצעת מחיר" key="bid">
                        <div className="bid-section">
                            <p className="order-information-card-additional-info-line">
                                * תנאי תשלום- הצעת המחיר ניתנת בהתאם לתנאי התשלום שמוגדרים במערכת.
                            </p>
                            <p className="order-information-card-additional-info-line">
                                * תוכן ההצעה- בית העסק מתחייב לעמוד בתוכן ההצעה כפי שנשלחה ללקוח, לרבות מחירים, כמויות,
                                תאריכים ושירותים שצוינו.
                            </p>
                            <p className="order-information-card-additional-info-line">
                                * תאריך האירוע- התאריך המופיע בהצעת המחיר אינו מהווה שמירה בפועל של המועד. התאריך יישמר
                                רק לאחר שהלקוח מאשר את ההצעה וההזמנה נכנסת למערכת.
                            </p>
                            <p className="order-information-card-additional-info-line">
                                * עריכה וביטול לאחר אישור ההצעה- לאחר שהלקוח אישר את ההצעה וההזמנה נכנסה למערכת, יחולו
                                עליה מדיניות העריכות והביטולים כפי שתקפה להזמנות אחרות במערכת.
                            </p>
                        </div>
                    </Panel>
                </Collapse>
                <StyledButton
                    type="primary"
                    size="large"
                    block
                    className="save-bid-btn"
                    onClick={() => form.submit()}
                    disabled={isUploading}
                    loading={isUploading}>
                    שליחת הצעת מחיר ללקוח
                </StyledButton>
            </Form>
        </div>
    );
};
